import React from 'react';
import classNames from 'classnames';
import { compose } from 'redux';
import { ArrowUpRight } from 'lucide-react';

import { NamedLink } from '../../components';
import { StaticPage } from '../PageBuilder/PageBuilder';
import TopbarContainer from '../TopbarContainer/TopbarContainer';
import FooterContainer from '../FooterContainer/FooterContainer';
import useRevealOnReady from '../../hooks/useRevealOnReady';
import revealCss from '../../styles/contentReveal.module.css';

import css from './AboutPage.module.css';

const PAIN_POINTS = [
  {
    number: '01',
    title: 'False Advertising',
    body:
      "I ordered a peach silk dress. I received orange polyester. The color was wrong, the fabric was cheap, and the return window had already closed. This isn't a one-off - it's practically the business model.",
  },
  {
    number: '02',
    title: 'Model vs. Me',
    body:
      "Turns out I'm not built like a 6-foot model. Shocking. The real problem is that pieces can look completely different in real life - sometimes better, sometimes catastrophically worse - and there's no way to know until it arrives and you've already paid for shipping.",
  },
  {
    number: '03',
    title: 'The Final Sale Death Trap',
    body:
      "Designer pieces at a discount are irresistible. Final sale with no returns and zero honest information about fit? That's not a deal. That's a gamble. I kept losing.",
  },
  {
    number: '04',
    title: 'Inconsistent Size Charts',
    body:
      "I'm 135lbs and 5'6\" with a generally hourglass figure. I've worn a size 2 and a size 8 - sometimes in the same week. The size charts aren't lying, exactly. They're just not telling the truth either.",
  },
  {
    number: '05',
    title: 'The Returns Hassle',
    body:
      "It sounds simple. Return windows close faster than you'd think, the post office assumes you have nothing better to do at 2pm on a Tuesday, and customer service negotiations are a special kind of purgatory.",
  },
  {
    number: '06',
    title: 'Sustainability (or Lack of)',
    body:
      "I'm not an environmental activist. But multiply my return rate by every woman playing the same online shopping roulette and the math gets grim. The fashion industry doesn't need more returns. It needs better information upfront.",
  },
];

const PLATFORM_ITEMS = [
  {
    verdict: 'Theirs',
    title: 'The fast fashion platforms',
    body:
      'Great for volume. Requires constant social media engagement. Not curated. Not for the kind of pieces you actually want to find.',
  },
  {
    verdict: 'Theirs',
    title: 'The luxury consignment sites',
    body:
      'Good authentication. Terrible payouts. They take a commission that would make a real estate agent blush.',
  },
  {
    verdict: 'Ours',
    title: 'Unvarnished',
    body:
      'Curated contemporary and luxury pieces from professional wardrobes. 80% back to the seller. Honest descriptions. No fast fashion, no flippers, no drama.',
    ours: true,
  },
];

const AboutPageComponent = props => {
  const schemaTitle = 'About Us';
  const schemaDescription = 'Learn more about Unvarnished and our mission';
  const hasRevealedContent = useRevealOnReady(true);
  const revealedContentClassName = classNames(revealCss.reveal, {
    [revealCss.hidden]: !hasRevealedContent,
    [revealCss.visible]: hasRevealedContent,
  });

  return (
    <StaticPage
      title={schemaTitle}
      description={schemaDescription}
      schema={{
        '@context': 'http://schema.org',
        '@type': 'WebPage',
        description: schemaDescription,
        name: schemaTitle,
      }}
    >
      <TopbarContainer />
      <div className={revealedContentClassName}>
        <div className={css.root}>
          <main className={css.main}>
            <section className={css.openingSection}>
              <div className={css.outer}>
                <div className={css.openingGrid}>
                  <div>
                    <p className={css.openingEyebrow}>About Unvarnished</p>
                    <h1 className={css.openingQuote}>
                      Fashion&apos;s dirty little secret is that{' '}
                      <span>nobody tells you the truth.</span>
                    </h1>
                  </div>
                  <p className={css.openingBody}>
                    I created Unvarnished because I was tired of online shopping feeling like a
                    guessing game. Colors that looked nothing like the photos, sizing that seemed
                    totally random, fit that was anybody&apos;s guess. So I built the thing that
                    should have existed already.
                  </p>
                </div>
              </div>
            </section>

            <div className={css.sectionDivider} />

            <section className={css.chapterSection}>
              <div className={css.outer}>
                <header className={css.chapterHeader}>
                  <span className={css.chapterNumber}>I</span>
                  <span className={css.chapterLabel}>Chapter One</span>
                  <h2 className={css.chapterTitle}>
                    In which I get very tired of lying product descriptions.
                  </h2>
                </header>

                <div className={css.painList}>
                  {PAIN_POINTS.map(item => (
                    <article key={item.number} className={css.painItem}>
                      <div className={css.painNumber}>{item.number}</div>
                      <div>
                        <h3 className={css.painTitle}>{item.title}</h3>
                        <p className={css.painBody}>{item.body}</p>
                      </div>
                    </article>
                  ))}
                </div>

                <div className={css.chapterConclusion}>
                  <h3 className={css.conclusionStatement}>
                    So I built <span>The Research Archive.</span> No ring lights. No filters. Just
                    the truth.
                  </h3>
                  <div className={css.conclusionBodyWrap}>
                    <p className={css.conclusionBody}>
                      Real reviews from real women, on real bodies, in real light. 800+ honest
                      reviews of contemporary brands - not for sale, just research. Because you
                      deserve to know what you&apos;re buying before you&apos;re stuck with it.
                    </p>
                    <NamedLink name="ReviewsPage" className={css.inlineLink}>
                      See what real reviews look like
                    </NamedLink>
                  </div>
                </div>
              </div>
            </section>

            <section className={css.chapterTwoSection}>
              <div className={css.outer}>
                <header className={css.chapterHeader}>
                  <span className={css.chapterNumber}>II</span>
                  <span className={css.chapterLabel}>Chapter Two</span>
                  <h2 className={css.chapterTitle}>
                    In which I decide to do something about the wardrobe problem too.
                  </h2>
                </header>

                <div className={css.resaleGrid}>
                  <div>
                    <p className={css.resaleBody}>
                      My inbox started filling with messages asking about selling opportunities. It
                      felt like a natural next step - and the existing options were, frankly, not
                      great. Other platforms became cluttered free-for-alls. Luxury consignment
                      sites took commissions that would make a real estate agent blush. So here we
                      are.
                    </p>
                    <p className={css.resaleBody}>
                      Curated pieces from real wardrobes, honest descriptions, and 80% back to the
                      seller. Novel concept, apparently.
                    </p>
                    <NamedLink name="StartSellingSignupPage" className={css.ctaButton}>
                      <ArrowUpRight aria-hidden="true" className={css.ctaIcon} strokeWidth={2} />
                      <span>Start Selling</span>
                    </NamedLink>
                  </div>

                  <div className={css.platformList}>
                    {PLATFORM_ITEMS.map(item => (
                      <article key={item.title} className={css.platformItem}>
                        <div
                          className={
                            item.ours
                              ? `${css.platformVerdict} ${css.platformVerdictOurs}`
                              : css.platformVerdict
                          }
                        >
                          {item.verdict}
                        </div>
                        <div>
                          <h3 className={css.platformName}>{item.title}</h3>
                          <p className={css.platformBody}>{item.body}</p>
                          {item.ours ? (
                            <NamedLink name="ResalePage" className={css.inlineLink}>
                              Browse current listings
                            </NamedLink>
                          ) : null}
                        </div>
                      </article>
                    ))}
                  </div>
                </div>
              </div>
            </section>

            <section className={css.contactSection}>
              <div className={css.outer}>
                <div className={css.contactInner}>
                  <div>
                    <p className={css.contactHeading}>
                      Thoughts? Feedback? Strong opinions about sizing?
                    </p>
                    <p className={css.contactBody}>
                      Constructive feedback is always welcome - and occasionally acted upon.
                    </p>
                  </div>
                  <a className={css.contactLink} href="mailto:hello@unvarnished.clothing">
                    hello@unvarnished.clothing
                  </a>
                </div>
              </div>
            </section>
          </main>
        </div>
        <FooterContainer />
      </div>
    </StaticPage>
  );
};

export default compose()(AboutPageComponent);
